import React, { useEffect, useMemo, useState } from 'react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';

import {
  DrawerContainer,
  DrawerBody,
  SearchInput,
  OptionList,
  OptionItem,
  OptionItemLabel,
  SelectedDot,
  EmptyState,
  FormFooter,
  SubmitButton,
} from './styles';

export interface MultiSelectOption {
  code: string;
  name: string;
}

interface Props {
  show: boolean;
  title: string;
  options: MultiSelectOption[];
  initialSelected: string[];
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: (selected: string[]) => void;
}

const MultiSelectList = ({ show, title, options, initialSelected, isLoading, onClose, onConfirm }: Props) => {
  const { t } = useStoreTranslation();
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (show) {
      setSelected(initialSelected);
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.name.toLowerCase().includes(term));
  }, [options, search]);

  const handleToggle = (code: string) => {
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const handleConfirm = () => {
    onConfirm(selected);
  };

  return (
    <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
      <DrawerContainer>
        <Mobile>
          <AppBar noGoBack rightIcon="/corporate/images/close.svg" rightAction={onClose}>
            {title}
          </AppBar>
        </Mobile>
        <Desktop>
          <ModalHeader title={title} onClose={onClose} />
        </Desktop>

        <DrawerBody>
          <SearchInput
            type="text"
            placeholder={t('paymentException.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {isLoading ? (
            <Loader />
          ) : filteredOptions.length === 0 ? (
            <EmptyState>{t('paymentException.empty')}</EmptyState>
          ) : (
            <OptionList>
              {filteredOptions.map((option) => (
                <OptionItem key={option.code} onClick={() => handleToggle(option.code)}>
                  <OptionItemLabel>{option.name}</OptionItemLabel>
                  {selected.includes(option.code) && <SelectedDot />}
                </OptionItem>
              ))}
            </OptionList>
          )}
        </DrawerBody>

        <FormFooter>
          <SubmitButton type="button" value={t('paymentException.confirmSelection')} onClick={handleConfirm} />
        </FormFooter>
      </DrawerContainer>
    </ApplicationOverlayContent>
  );
};

export default MultiSelectList;
